import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export enum ETreeSimulationStatus {
  Pending = 'Pending',
  Processing = 'Processing',
  Completed = 'Completed',
  Failed = 'Failed',
}

export interface TreeSimulationCommandDto {
  id: string
  name: string
  financingProgramId: string
  startDate: string
  endDate: string
  applicantTreeId: string
  coapplicantTreeId: string | null
  status: ETreeSimulationStatus
  createdByUserFullname: string
  createdOn: string
  processedCount: number
  totalCount: number
  errorCount: number
  errorMessage: string | null
  resultBlobPath: string | null
}

export interface DecisionTreeInfoDto {
  id: string
  name: string
  description: string
  latestPublishedVersion: string
}

export interface DecisionTreeVersionInfoDto {
  id: string
  decisionTreeId: string
  version: number
  comment: string
  createdOn: string
  updatedOn: string
  publishedOn: string | null
}

export interface CreateTreeSimulationCommandDto {
  name: string
  financingProgramId: string
  startDate: string
  endDate: string
  applicantTreeId: string
  coapplicantTreeId: string | null
}

const baseUrl = '/TreeSimulations'

const treeSimulationKeys = {
  all: ['treeSimulations'] as const,
  list: (pageIndex: number, pageSize: number) => [...treeSimulationKeys.all, 'list', pageIndex, pageSize] as const,
  trees: () => [...treeSimulationKeys.all, 'trees'] as const,
  treeVersions: (treeId: string) => [...treeSimulationKeys.all, 'treeVersions', treeId] as const,
}

async function getTreeSimulations(pageIndex: number, pageSize: number): Promise<TreeSimulationCommandDto[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<TreeSimulationCommandDto[]>(`${baseUrl}/Commands`, { params: { pageIndex, pageSize } })
  return response.data
}

async function getDecisionTrees(): Promise<DecisionTreeInfoDto[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<DecisionTreeInfoDto[]>(`${baseUrl}/trees`)
  return response.data
}

async function getDecisionTreeVersions(treeId: string): Promise<DecisionTreeVersionInfoDto[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<DecisionTreeVersionInfoDto[]>(`${baseUrl}/trees/${treeId}/versions`)
  return response.data.sort((a, b) => b.version - a.version)
}

async function createTreeSimulation(dto: CreateTreeSimulationCommandDto): Promise<TreeSimulationCommandDto> {
  const apiClient = getApiClient()
  const response = await apiClient.post<TreeSimulationCommandDto>(`${baseUrl}/Commands`, dto)
  return response.data
}

async function downloadSimulationResult(id: string): Promise<string> {
  const apiClient = getApiClient()
  const response = await apiClient.get<string>(`${baseUrl}/Commands/${id}/download`)
  return response.data
}

export function useTreeSimulations(pageIndex: number, pageSize: number): [TreeSimulationCommandDto[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: treeSimulationKeys.list(pageIndex, pageSize),
    queryFn: () => getTreeSimulations(pageIndex, pageSize),
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

export function useDecisionTreeVersions(treeId: string | null): [DecisionTreeVersionInfoDto[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: treeSimulationKeys.treeVersions(treeId ?? ''),
    queryFn: () => getDecisionTreeVersions(treeId!),
    enabled: !!treeId,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

export function useDecisionTrees(): [DecisionTreeInfoDto[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: treeSimulationKeys.trees(),
    queryFn: getDecisionTrees,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

export function useCreateTreeSimulation(): [MutationFunction<TreeSimulationCommandDto, CreateTreeSimulationCommandDto>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createTreeSimulation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: treeSimulationKeys.all })
    },
  })

  return [mutateAsync, isPending]
}

export function useDownloadSimulationResult(): [MutationFunction<string, string>, boolean] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: downloadSimulationResult,
  })

  return [mutateAsync, isPending]
}
